import React, { forwardRef } from "react"
import { Tabs } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import ReportTableContent from "./ReportTableContent.component"
import type { ReportTableContentRef } from "./ReportTableContent.component"
import ReportTabTitle from "./ReportTabTitle.component"
import { useReportTableDisplay } from "features/Reporting/hooks/ReportTable/useReportTableDisplay"
import type { TableRef } from "features/Reporting/hooks/ReportTable/useReportTableDisplay"

// Types
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import {
  ExcelTab,
  ExcelReportingRef,
  ExcelTableData,
  TabSettings,
} from "types/ReportingDashboard/reporting.types"

/**
 * Props interface for ReportTableDisplay component
 */
export interface ReportTableDisplayProps {
  /** Data codebook for variable information */
  codebook: CodebookData
  /** Query instance for data operations */
  queryInstance: QueryInstance | null
  /** Current filter configuration */
  filter: string
  /** Auto-run script content */
  autoRunScriptContent: string
  /** Configuration for all tabs */
  tabsConfig: ExcelTab[]
  /** Callback when tabs configuration changes */
  onTabsConfigChange: (newTabsConfig: ExcelTab[]) => void
}

/**
 * Component to display report tables within editable tabs.
 * Allows adding, removing, and renaming tabs, each containing a ReportTableContent instance.
 * Configuration for tabs is received from parent.
 * Implements sequential execution to prevent server crashes from concurrent script runs.
 */
const ReportTableDisplay = forwardRef<
  ExcelReportingRef,
  ReportTableDisplayProps
>(
  (
    {
      codebook,
      queryInstance,
      filter,
      autoRunScriptContent,
      tabsConfig,
      onTabsConfigChange,
    },
    ref
  ) => {
    const {
      // State
      activeKey,
      setActiveKey,
      memoizedInitialSettings,

      // Tab management
      onEdit,
      updateTabLabel,

      // Settings management
      handleTabSettingsChange,

      // Execution management
      handleExecutionRequest,

      // Export functionality
      getExcelTablesData,

      // Ref management
      registerTableRef,

      // Test method
      testRef,
    } = useReportTableDisplay({
      codebook,
      queryInstance,
      filter,
      autoRunScriptContent,
      tabsConfig,
      onTabsConfigChange,
    })

    // ========================================================================================
    // REF METHODS
    // ========================================================================================

    React.useImperativeHandle(
      ref,
      () => ({
        getExcelTablesData: async (): Promise<ExcelTableData[]> => {
          return await getExcelTablesData()
        },
        testRef: (): boolean => testRef(),
      }),
      [getExcelTablesData, testRef]
    )

    // ========================================================================================
    // RENDER
    // ========================================================================================

    return (
      <div className="flex flex-col h-full w-full bg-white rounded-md p-4">
        <Tabs
          type="editable-card"
          onChange={setActiveKey}
          activeKey={activeKey}
          onEdit={onEdit}
          addIcon={<FontAwesomeIcon icon={faPlus} />}
          className="reporting-excel-tabs w-full"
        >
          {(tabsConfig || []).map((tabItem) => {
            const initialSettings = memoizedInitialSettings.get(tabItem.key)!

            return (
              <Tabs.TabPane
                tab={
                  <ReportTabTitle
                    tabConfigItem={tabItem}
                    onLabelUpdate={updateTabLabel}
                  />
                }
                key={tabItem.key}
                closable={true}
                forceRender={true}
              >
                <ReportTableContent
                  key={`${tabItem.key}-content`}
                  tabKey={tabItem.key}
                  codebook={codebook}
                  queryInstance={queryInstance}
                  filter={filter}
                  ref={(tableRef: ReportTableContentRef | null) => {
                    registerTableRef(tabItem.key, tableRef as TableRef | null)
                  }}
                  autoRunScriptContent={autoRunScriptContent}
                  initialSettings={initialSettings}
                  onSettingsChange={(newSettings: TabSettings) =>
                    handleTabSettingsChange(tabItem.key, newSettings)
                  }
                  onRequestExecution={handleExecutionRequest}
                />
              </Tabs.TabPane>
            )
          })}
        </Tabs>
      </div>
    )
  }
)

ReportTableDisplay.displayName = "ReportTableDisplay"
export default ReportTableDisplay
